// services/school-ui/src/pages/promotion/api.ts
// Promotion-svc admin calls routed via the school-backend Bearer proxy
// at /api/school/admin/promotions/* (see services/school-backend/app/routers/
// admin_promotions.py). The direct /api/admin/promotions path 401s on the
// school-bosong nginx vhost because that vhost is Bearer-aware, not
// X-User-Id-stamping — promotion-svc itself is gateway-trust.

import type { PromotionPreview, PromotionRequest, Status } from "./types";
import { authHeaders } from "../../api/school";

const BASE = "/api/school/admin/promotions";

export class ForbiddenError extends Error {
  constructor() {
    super("forbidden");
    this.name = "ForbiddenError";
  }
}

export class StillIngestingError extends Error {
  constructor() {
    super("still ingesting");
    this.name = "StillIngestingError";
  }
}

export class ConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConflictError";
  }
}

export async function fetchPromotions(
  status: Status | "all",
): Promise<PromotionRequest[]> {
  const url = status === "all" ? BASE : `${BASE}?status=${status}`;
  const r = await fetch(url, { headers: authHeaders() });
  if (r.status === 403) throw new ForbiddenError();
  if (!r.ok) throw new Error("failed to load promotions");
  const body = await r.json();
  return body.requests ?? [];
}

export async function fetchPreview(id: string): Promise<PromotionPreview> {
  const r = await fetch(`${BASE}/${id}/preview`, { headers: authHeaders() });
  if (r.status === 403) throw new ForbiddenError();
  if (r.status === 423) throw new StillIngestingError();
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.error ?? "failed to load preview");
  }
  return r.json();
}

export async function decide(
  id: string,
  action: "approve" | "reject",
  notes?: string,
  targetCollection?: string,
): Promise<PromotionRequest> {
  const r = await fetch(`${BASE}/${id}/${action}`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: (notes || targetCollection)
      ? JSON.stringify({
          ...(notes ? { decision_notes: notes } : {}),
          ...(targetCollection ? { target_collection: targetCollection } : {}),
        })
      : undefined,
  });
  if (r.status === 409) {
    const body = await r.json().catch(() => ({}));
    throw new ConflictError(body.error ?? "conflict");
  }
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.error ?? `failed to ${action}`);
  }
  return r.json();
}

export async function revert(id: string): Promise<PromotionRequest> {
  const r = await fetch(`${BASE}/${id}/revert`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (r.status === 409) {
    const body = await r.json().catch(() => ({}));
    throw new ConflictError(body.error ?? "revert window elapsed");
  }
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.error ?? "revert failed");
  }
  return r.json();
}
